import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/layout/AdminLayout';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Search, Mail, Download } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletter = () => {
  const { token } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const response = await axios.get(`${API}/api/admin/newsletter`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscribers(response.data);
      } catch (error) {
        console.error('Error fetching subscribers:', error);
        toast.error('Failed to load subscribers');
      } finally {
        setLoading(false);
      }
    };
    fetchSubscribers();
  }, [token]);

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExport = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' + 
      'Email,Subscribed At\n' +
      subscribers.map(s => `${s.email},${s.subscribed_at}`).join('\n');
    
    const link = document.createElement('a');
    link.href = encodeURI(csvContent);
    link.download = 'blushandgo_subscribers.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Subscribers exported!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Newsletter Subscribers">
      <div className="space-y-6" data-testid="admin-newsletter">
        {/* Stats */}
        <div className="bg-white border p-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-purple-50 text-purple-600">
              <Mail className="w-8 h-8" />
            </div>
            <div>
              <p className="text-3xl font-semibold">{subscribers.length}</p>
              <p className="text-neutral-500">Total Subscribers</p>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 w-full sm:w-64 rounded-none"
              data-testid="search-subscribers-input"
            />
          </div>

          <Button
            onClick={handleExport}
            disabled={subscribers.length === 0}
            variant="outline"
            className="h-10 rounded-none"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="text-center py-20">
              <Mail className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
              <p className="text-neutral-500">
                {searchQuery ? 'No subscribers found' : 'No subscribers yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subscribed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscribers.map((subscriber, index) => (
                    <TableRow key={subscriber.id} data-testid={`subscriber-row-${index}`}>
                      <TableCell className="text-neutral-500">{index + 1}</TableCell>
                      <TableCell className="font-medium">{subscriber.email}</TableCell>
                      <TableCell className="text-neutral-500">
                        {formatDate(subscriber.subscribed_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;
